/**
 * disableCascade (#2388) — the lens enable/disable model for Preisspiegel.
 *
 * A tracked item (L3) is ENABLED iff it has ≥1 observed point (its tile is
 * clickable). The cascade rolls that up:
 *   - a Kategorie (L2) is disabled iff NONE of its items are enabled;
 *   - a Warengruppe (L1) is disabled iff ALL of its Kategorien are disabled.
 *
 * Visibility model (#2420): the catalog defines the *universe/structure*, but
 * DATA PRESENCE drives what the lens renders — a disabled Warengruppe/Kategorie
 * is HIDDEN, not greyed (no "keine Daten" rows). When nothing has data at all
 * (``hasAnyData`` is false) the page shows a first-class empty state instead.
 *
 * Pure + unit-tested. The page maps this state onto the design-system
 * ``LensePanel`` (count badge = enabled-item count; only enabled rows survive).
 */
import type { LenseItem, LenseSubItem } from '@hofsync/design-system';
import type { Warengruppe } from './catalog';
import { CATALOG } from './catalog';

/** Predicate: does this tracked item have any observed data? */
export type HasData = (itemId: string) => boolean;

export interface ItemState {
  id: string;
  name: string;
  enabled: boolean;
}

export interface KategorieState {
  id: string;
  name: string;
  enabled: boolean;
  enabledItemCount: number;
  items: ItemState[];
}

export interface WarengruppeState {
  id: string;
  name: string;
  enabled: boolean;
  enabledItemCount: number;
  kategorien: KategorieState[];
}

/** Compute the full enable/disable cascade for a catalog given a data predicate. */
export function buildCatalogState(hasData: HasData, catalog: Warengruppe[] = CATALOG): WarengruppeState[] {
  return catalog.map((wg) => {
    const kategorien = wg.kategorien.map((kat): KategorieState => {
      const items = kat.items.map((it): ItemState => ({ id: it.id, name: it.name, enabled: hasData(it.id) }));
      const enabledItemCount = items.filter((i) => i.enabled).length;
      return { id: kat.id, name: kat.name, enabled: enabledItemCount > 0, enabledItemCount, items };
    });
    const enabledItemCount = kategorien.reduce((sum, k) => sum + k.enabledItemCount, 0);
    return {
      id: wg.id,
      name: wg.name,
      enabled: kategorien.some((k) => k.enabled),
      enabledItemCount,
      kategorien,
    };
  });
}

/**
 * Map the cascade onto DS ``LenseItem[]`` (L1 rows → L2 sub-rows), HIDING the
 * empty parts of the catalog (#2420): a disabled Warengruppe is omitted
 * entirely, and within a kept Warengruppe every disabled Kategorie is dropped.
 * Data presence — not the catalog — drives visibility, so a zero-data
 * Kategorie/Warengruppe simply does not appear (no "keine Daten" rows). A
 * Kategorie that survives the filter is enabled by construction, so it always
 * carries its enabled-item count.
 */
export function buildLensItems(state: WarengruppeState[]): LenseItem[] {
  return state
    .filter((wg) => wg.enabled)
    .map((wg): LenseItem => ({
      id: wg.id,
      name: wg.name,
      count: wg.enabledItemCount || undefined,
      children: wg.kategorien
        .filter((kat) => kat.enabled)
        .map((kat): LenseSubItem => ({
          id: kat.id,
          name: kat.name,
          count: kat.enabledItemCount || undefined,
        })),
    }));
}

/** True iff at least one Warengruppe is data-bearing (the lens has rows to show). */
export function hasAnyData(state: WarengruppeState[]): boolean {
  return state.some((wg) => wg.enabled);
}

/** The first enabled Kategorie id across the whole tree, or null. */
export function firstEnabledKategorieId(state: WarengruppeState[]): string | null {
  for (const wg of state) {
    for (const kat of wg.kategorien) {
      if (kat.enabled) return kat.id;
    }
  }
  return null;
}

/** Locate a Kategorie state (+ its parent) by id. */
export function findKategorieState(
  state: WarengruppeState[],
  katId: string,
): { warengruppe: WarengruppeState; kategorie: KategorieState } | null {
  for (const warengruppe of state) {
    const kategorie = warengruppe.kategorien.find((k) => k.id === katId);
    if (kategorie) return { warengruppe, kategorie };
  }
  return null;
}

/**
 * Resolve the selected Kategorie id: the ``?kat=`` param ONLY when it names an
 * ENABLED (data-bearing) Kategorie, else the first enabled Kategorie, else null.
 * A ``?kat=`` naming a now-hidden (empty) Kategorie falls through — it never
 * resurrects a ghost "Keine Daten" grid (#2420).
 */
export function resolveSelectedKategorie(param: string | null, state: WarengruppeState[]): string | null {
  if (param) {
    const loc = findKategorieState(state, param);
    if (loc?.kategorie.enabled) return param;
    // HOF-299 S3 — a WARENGRUPPE id (the retired Kategorie ids `saatgut`,
    // `duengemittel`, `pflanzenschutz`, `maschinen_arbeit`, `pflanzgut` all are one
    // now) resolves to its first data-bearing Kategorie, the same rule `/tiles`
    // applies to it. An empty Warengruppe falls through like an empty Kategorie.
    const wg = state.find((w) => w.id === param);
    const first = wg?.kategorien.find((k) => k.enabled);
    if (first) return first.id;
  }
  return firstEnabledKategorieId(state);
}
